import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEvent, SortMeta } from 'primeng/api';
import { map, tap } from 'rxjs/operators';
import { DefaultSort } from '../../log-settings/log-settings.model';
import { LogService } from '../../logs/log.service';
import { Report } from '../../reports/report.model';
import { ReportService } from '../../reports/report.service';
import { HALResponse } from '../../shared/model/app.model';
import { AppStateService } from '../../core/services/app-state.service';
import { NotesTableDataRow } from './notes-table.model';

@Injectable()
export class NotesTableService {
	get activeNoteFilterReportId(): number | null | undefined {
		if (this.reportService.enterNotesFilterReportId !== undefined)
			return this.reportService.enterNotesFilterReportId;

		const selectedLog = LogService.selectedLog;
		
		if (selectedLog) {
			return selectedLog.activeLogUser.suppressReportFilter ? null :
				selectedLog.activeLogUser.defaultReportFilter?.id || 
				selectedLog.defaultReportFilter?.id;
		}

		return;
	}
	set activeNoteFilterReportId(reportFilter) {
		this.reportService.enterNotesFilterReportId = reportFilter;
	}

	static get defaultToAbridgedKey() {
        return `${LogService.selectedLog?.id}-notes-table-default-abridged`;
	}

	constructor(
		private http: HttpClient,
		private reportService: ReportService
	) { }

	getReportFilters() {
		return this.http.get<HALResponse<{ reports: Report[] }>>('/api/reports', {
			params: {
				log: LogService.selectedLog!.id.toString(),
				isViewNotesFilter: 'true',
				activeRevision: 'true',
				projection: 'reportFilterProjection'
			}
		})
		.pipe(
			map(r => r._embedded.reports),
			tap(reports => {
				// Clear active report filter if it has been configured to not be used as a filter
				if (this.activeNoteFilterReportId && !reports.some(r => r.id == this.activeNoteFilterReportId))
					this.activeNoteFilterReportId = null;
			})
		)
	}

	static getDefaultSorts() {
		const noteView = AppStateService.urlSearchParamsHas('readOnlyView') ? 'recent' : 'enter';

		const defaultSortString = LogService.getConfigValueFor(`default_sort.${noteView}_notes`),
			defaultSorts = defaultSortString ?
				(JSON.parse(defaultSortString) as DefaultSort[]).filter(ds => ds.sort) :
				[];

		if (defaultSorts.length) {
			return defaultSorts.map(ds => ({
				field: ds.sort == 'noteTime' ? 'timeframe' : ds.sort,
				order: ds.dir == 'desc' ? -1 : 1
			} as SortMeta));
		}

		return [{ field: 'timeframe', order: -1 } as SortMeta];
	}

	sortNotes(event: SortEvent) {
		const currentTime = new Date().getTime();
		event.data?.sort(sort);

		function sort(a: any, b: any): number {
			const n1: NotesTableDataRow = a,
				n2: NotesTableDataRow = b

			const sortEvents = (event.multiSortMeta ?? []).map(sortMeta => {
				switch (sortMeta.field) {
					case 'timeframe':
						return () => {
							let val = 0;

							let n1Time = ((n1.carryForward && sortMeta.order < 0)
								? new Date(n1.carryForwardEndTime ?? n1.timeframe)
								: n1.noteTimeDate)
								.getTime();

							let n2Time = ((n2.carryForward && sortMeta.order < 0)
								? new Date(n2.carryForwardEndTime ?? n2.timeframe)
								: n2.noteTimeDate)
								.getTime();

							if (val === 0) {
								if (n1Time > n2Time)
									val = 1;
								else if (n1Time < n2Time)
									val = -1;
							}

							return val * sortMeta.order;
						}
					case 'maxPriority':
						return () => {
							let val = 0;

							if (n1.maxPriority > n2.maxPriority)
								val = 1;
							else if (n1.maxPriority < n2.maxPriority)
								val = -1;
							return val * sortMeta.order;
						}
					case 'createdBy':
					case 'updatedBy':
						const field = sortMeta.field as 'createdBy' | 'updatedBy';
						return () => {
							const sortByName = (nameKey: 'firstName' | 'lastName') => {
								let _val = 0;
								if (n1[field][nameKey].localeCompare(n2[field][nameKey]) > 0)
									_val = 1;
								else if (n1[field][nameKey].localeCompare(n2[field][nameKey]) < 0)
									_val = -1;
								return _val;
							}
							let val = sortByName('lastName');
							val = val !== 0 ? val : sortByName('firstName');

							return val * sortMeta.order;
						}
					default:
						return () => 0
				}
			});

			let val = 0;
			for (let i = 0; i < sortEvents.length; i++) {
				val = sortEvents[i]();
				if (val !== 0) break;
			}

			return val;
		}
	}
}